<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignPlanDialog from './CampaignPlanDialog.vue';
import CampaignPlanList from './CampaignPlanList.vue';
import ConfirmDeleteCampaignPlanDialog from './ConfirmDeleteCampaignPlanDialog.vue';

const { t } = useI18n();
const store = useStore();

const [showDialog, toggleDialog] = useToggle();
const selectedPlan = ref(null);
const confirmDeleteDialogRef = ref(null);

const campaignPlans = computed(() => store.getters['campaignPlans/getCampaignPlans']);
const uiFlags = computed(() => store.getters['campaignPlans/getUIFlags']);
const isFetching = computed(() => uiFlags.value.isFetching);
const hasNoCampaignPlans = computed(() => campaignPlans.value?.length === 0 && !isFetching.value);

onMounted(() => {
  store.dispatch('campaignPlans/get');
});

const handleCreate = () => {
  toggleDialog(true);
};

const handleClose = () => {
  toggleDialog(false);
  store.dispatch('campaignPlans/get');
};

const handleDelete = async plan => {
  selectedPlan.value = plan;
  confirmDeleteDialogRef.value.dialogRef.open();
};

const handleConfirmDelete = async () => {
  try {
    await store.dispatch('campaignPlans/delete', selectedPlan.value.id);
    useAlert(t('CAMPAIGN_PLANS.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN_PLANS.DELETE.ERROR'));
  } finally {
    selectedPlan.value = null;
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN_PLANS.HEADER')"
    :button-label="t('CAMPAIGN_PLANS.NEW_BUTTON')"
    @click="handleCreate()"
    @close="toggleDialog(false)"
  >
    <template #action>
      <CampaignPlanDialog v-if="showDialog" @close="handleClose" />
    </template>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <CampaignPlanList
      v-else-if="!hasNoCampaignPlans"
      :campaign-plans="campaignPlans"
      @delete="handleDelete"
    />

    <div
      v-else
      class="flex flex-col items-center justify-center py-20"
    >
      <div class="text-center max-w-md">
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ t('CAMPAIGN_PLANS.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN_PLANS.EMPTY_STATE.SUBTITLE') }}
        </p>
      </div>
    </div>

    <ConfirmDeleteCampaignPlanDialog
      ref="confirmDeleteDialogRef"
      :selected-plan="selectedPlan"
      @confirm="handleConfirmDelete"
    />
  </CampaignLayout>
</template>

